import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../helpers/api";
import { ShowToast } from "../../../components/toast";
import { BillingReconciliation, BillingSyncResult, BulkReconciliation, StripeCustomerWithPlan, StripePlansResponse, StripeSubscription } from "../types";
import { isTotpError } from "../components/totp-error";

// =============================================================================
// QUERY HOOKS
// =============================================================================

export const useBillingList = (month: string, page: number, limit: number) => {
  return useQuery({
    queryKey: ["commission-billing", month, page, limit],
    queryFn: () => api.commissionBilling.getBillingList(month, page, limit),
    enabled: true,
    refetchOnWindowFocus: true,
  });
};

export const useBillingSummary = (month: string) => {
  return useQuery({
    queryKey: ["commission-billing-summary", month],
    queryFn: () => api.commissionBilling.getSummary(month),
    enabled: true,
    refetchOnWindowFocus: true,
  });
};

// =============================================================================
// MUTATION HOOKS
// =============================================================================

export type SetRatePayload = {
  userId: string;
  commissionRate: number;
  billingEmail: string;
  billingType?: 'fixed' | 'commission' | 'hybrid' | 'commission_or_flat';
  displayBasePrice?: number;
  displayCommissionMarkup?: number;
  showOnboardingFee?: boolean;
  isTestUser?: boolean;
  showReputationManagementWaiver?: boolean;
  platformFeeAmount?: number;
  platformFeeDescription?: string;
  minimumFeeAmount?: number;
  _silentToast?: boolean;
};

export const useSetCommissionRate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SetRatePayload) => {
      const { _silentToast, ...payload } = data;
      return api.commissionBilling.setCommissionRate(payload);
    },
    onSuccess: (_data, variables) => {
      if (!variables._silentToast) {
        ShowToast({ type: "success", message: "Commission rate updated" });
      }
      queryClient.invalidateQueries({ queryKey: ["commission-billing"] });
      queryClient.invalidateQueries({ queryKey: ["stripe-plans"] });
      // The rate feeds the projected bill in the customer-email preview, which
      // is cached with a long staleTime — drop it so a reopen re-renders.
      queryClient.invalidateQueries({
        queryKey: ["invoice-email-preview", variables.userId],
      });
    },
    onError: (error: any) => {
      ShowToast({ type: "error", message: error.response?.data?.message || "Failed to update rate" });
    },
  });
};

export const useSetAddonConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      userId: string;
      addonType: string;
      monthlyPrice?: number;
      startDate?: string;
      trialEnabled?: boolean;
      enabled?: boolean;
      // When set, suppress this mutation's own toast so the caller can show a
      // single combined toast after sequencing it with the rate save.
      _silentToast?: boolean;
    }) => {
      const { _silentToast, ...payload } = data;
      return api.commissionBilling.setAddonConfig(payload);
    },
    onSuccess: (_data, variables) => {
      if (!variables._silentToast) {
        ShowToast({ type: "success", message: "Add-on updated" });
      }
      queryClient.invalidateQueries({ queryKey: ["commission-billing"] });
      queryClient.invalidateQueries({ queryKey: ["commission-billing-enrich"] });
      // The add-on price/dates/trial drive the invoice email preview's projected
      // bill, so invalidate it here — otherwise the preview shows the pre-edit
      // invoice until its short cache expires.
      queryClient.invalidateQueries({
        queryKey: ["invoice-email-preview", variables.userId],
      });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to update add-on",
      });
    },
  });
};

export const useChargeClient = (
  onSuccess?: (
    // The charged/issued CommissionCharge — carries the pay link on send_invoice.
    data: { stripeHostedInvoiceUrl?: string | null },
    variables: { collectionMethod?: "charge_automatically" | "send_invoice" },
  ) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      userId: string;
      month: number;
      year: number;
      customAmount?: number;
      invoiceNote?: string;
      acknowledgeRateMismatch?: boolean;
      sendInvoiceEmail?: boolean;
      acknowledgeRecharge?: boolean;
      collectionMethod?: "charge_automatically" | "send_invoice";
      daysUntilDue?: number;
      totpCode?: string;
    }) => api.commissionBilling.chargeClient(data),
    onSuccess: (data, variables) => {
      const invoiced = variables.collectionMethod === "send_invoice";
      ShowToast({
        type: "success",
        message: invoiced
          ? variables.sendInvoiceEmail
            ? "Invoice sent — awaiting customer payment"
            : "Invoice created — awaiting customer payment (no email sent)"
          : "Card charged successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["commission-billing"] });
      queryClient.invalidateQueries({ queryKey: ["commission-billing-enrich"] });
      // After billing, the preview switches from a live projection to the
      // frozen bill of the recorded charge — invalidate so it re-renders.
      queryClient.invalidateQueries({
        queryKey: ["invoice-email-preview", variables.userId],
      });
      onSuccess?.(data, variables);
    },
    onError: (error: any, variables) => {
      if (isTotpError(error)) return;
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message ||
          (variables.collectionMethod === "send_invoice"
            ? "Failed to send invoice"
            : "Failed to charge card"),
      });
    },
  });
};

// Books the period's charge and emails the advance-notice invoice. No money
// moves here — the card on file is billed by the daily due pass once the notice
// window elapses, which is why this rides the same TOTP unlock as a charge.
export const useScheduleInvoiceNotice = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      userId: string;
      month: number;
      year: number;
      noticeDays?: number;
    }) => api.commissionBilling.scheduleInvoiceNotice(data),
    onSuccess: (data) => {
      // America/New_York, matching the backend's formatChargeOnLabel — the date
      // the customer's email quotes. In the admin's own zone a west-coast
      // browser would read a day earlier than what was actually promised.
      const chargeOn = data?.chargeAfter
        ? new Date(data.chargeAfter).toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
            timeZone: "America/New_York",
          })
        : null;
      ShowToast({
        type: "success",
        message: chargeOn
          ? `Invoice emailed — the card on file is charged ${chargeOn}`
          : "Invoice emailed — the card on file is charged after the notice period",
      });
      queryClient.invalidateQueries({ queryKey: ["commission-billing"] });
      queryClient.invalidateQueries({ queryKey: ["commission-billing-enrich"] });
      onSuccess?.();
    },
    onError: (error: any) => {
      if (isTotpError(error)) return;
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message || "Failed to schedule the invoice",
      });
    },
  });
};

// Voids an INVOICED charge's open Stripe invoice (closes the customer's
// payment page) and frees the period for re-billing.
export const useVoidInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chargeId: string) =>
      api.commissionBilling.voidInvoice(chargeId),
    onSuccess: () => {
      ShowToast({
        type: "success",
        message: "Invoice voided — the period can be billed again",
      });
      queryClient.invalidateQueries({ queryKey: ["commission-billing"] });
      // The voided period flips back to a live projection.
      queryClient.invalidateQueries({ queryKey: ["invoice-email-preview"] });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to void invoice",
      });
    },
  });
};

// Sends the standalone invoice email for a billed period (idempotent — a
// period whose email already went out comes back "skipped"; one Stripe already
// shows paid is settled server-side and comes back "already_paid", unsent).
export const useSendInvoiceEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { userId: string; month: number; year: number }) =>
      api.commissionBilling.sendInvoiceEmail(data),
    onSuccess: (data) => {
      const messages = {
        sent: { type: "success", message: "Invoice email sent" },
        skipped: {
          type: "success",
          message: "Invoice email already sent for this period",
        },
        failed: {
          type: "error",
          message: "Invoice email failed to send — check Slack alerts",
        },
        already_paid: {
          type: "success",
          message: "Already paid in Stripe — status updated, email not sent",
        },
      } as const;
      ShowToast(messages[data.result] ?? messages.failed);
      if (data.result === "already_paid") {
        queryClient.invalidateQueries({ queryKey: ["commission-billing"] });
        queryClient.invalidateQueries({
          queryKey: ["commission-billing-summary"],
        });
        queryClient.invalidateQueries({ queryKey: ["invoice-email-preview"] });
      }
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message || "Failed to send invoice email",
      });
    },
  });
};

export const useRetryCharge = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { chargeId: string; totpCode?: string }) =>
      api.commissionBilling.retryCharge(vars.chargeId, vars.totpCode),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Charge retry successful" });
      queryClient.invalidateQueries({ queryKey: ["commission-billing"] });
      // A successful retry settles the charge → preview flips to the frozen
      // receipt. Only chargeId is available, so drop every cached preview.
      queryClient.invalidateQueries({ queryKey: ["invoice-email-preview"] });
    },
    onError: (error: any) => {
      if (isTotpError(error)) return;
      ShowToast({ type: "error", message: error.response?.data?.message || "Failed to retry charge" });
    },
  });
};

export const useResetChargeForRebill = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chargeId: string) =>
      api.commissionBilling.resetChargeForRebill(chargeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["commission-billing"] });
      // Reset flips the period back to a live projection (the frozen receipt is
      // gone). The mutation only carries chargeId, so drop every cached preview;
      // they're cheap read-only re-renders.
      queryClient.invalidateQueries({ queryKey: ["invoice-email-preview"] });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message || "Failed to reset charge for re-billing",
      });
    },
  });
};

export const useSyncPaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => api.commissionBilling.syncPaymentMethod(userId),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Payment method synced" });
      queryClient.invalidateQueries({ queryKey: ["commission-billing"] });
    },
    onError: (error: any) => {
      ShowToast({ type: "error", message: error.response?.data?.message || "Failed to sync payment method" });
    },
  });
};

// Read-only check: does the app commission rate match the Stripe discount?
// Triggered explicitly from the Sync button (mutation, not auto-query) so we
// never hit Stripe on every modal open.
export const useReconcileBilling = () => {
  return useMutation({
    mutationFn: (userId: string): Promise<BillingReconciliation> =>
      api.commissionBilling.reconcileBilling(userId),
    onError: (error: any) => {
      ShowToast({ type: "error", message: error.response?.data?.message || "Failed to reconcile with Stripe" });
    },
  });
};

// Bulk reconcile across all configured billing customers for the "Sync All"
// popup. Read-only; triggered explicitly on button click.
export const useReconcileAll = () => {
  return useMutation({
    mutationFn: (): Promise<BulkReconciliation> =>
      api.commissionBilling.reconcileAll(),
    onError: (error: any) => {
      ShowToast({ type: "error", message: error.response?.data?.message || "Failed to reconcile with Stripe" });
    },
  });
};

// Apply an unambiguous Stripe drift into the app rate (app is source of truth;
// Stripe is never written). The backend only applies on a single-rate drift.
// Cache invalidation only — each caller owns its own success message (the
// per-customer ClientDetailModal and the bulk SyncAllModal show different copy).
export const useSyncFromStripe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string): Promise<BillingSyncResult> =>
      api.commissionBilling.syncFromStripe(userId),
    onSuccess: (result, userId) => {
      if (!result.applied) return;
      queryClient.invalidateQueries({ queryKey: ["commission-billing"] });
      queryClient.invalidateQueries({ queryKey: ["stripe-plans"] });
      // The rate feeds the projected bill in the cached customer-email preview.
      queryClient.invalidateQueries({ queryKey: ["invoice-email-preview", userId] });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message || "Failed to sync from Stripe",
      });
    },
  });
};

export const useLinkStripeCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { userId: string; stripeCustomerId: string }) =>
      api.commissionBilling.linkStripeCustomer(data),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Stripe customer linked successfully" });
      queryClient.invalidateQueries({ queryKey: ["commission-billing"] });
    },
    onError: (error: any) => {
      ShowToast({ type: "error", message: error.response?.data?.message || "Failed to link Stripe customer" });
    },
  });
};

// =============================================================================
// ENRICHMENT HOOK (Stripe PM + Google Ads — progressive loading)
// =============================================================================

export const useBillingEnrichment = (
  userIds: string[],
  month: string,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["commission-billing-enrich", month, ...userIds.sort()],
    queryFn: () => api.commissionBilling.enrichBillingData(userIds, month),
    enabled: enabled && userIds.length > 0,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
    // Refetches with the list on focus (only once stale) so cached Stripe fields never overwrite fresh rows.
    refetchOnWindowFocus: true,
  });
};

// =============================================================================
// STRIPE PLANS HOOKS
// =============================================================================

export const useStripePlans = (limit: number, cursor?: string) => {
  return useQuery<StripePlansResponse>({
    queryKey: ["stripe-plans", limit, cursor],
    queryFn: () => api.commissionBilling.getStripePlans(limit, cursor),
    refetchOnWindowFocus: false,
  });
};

export const useAllStripePlans = () => {
  return useQuery<StripeCustomerWithPlan[]>({
    queryKey: ["stripe-plans-all"],
    queryFn: async () => {
      const all: StripeCustomerWithPlan[] = [];
      let cursor: string | undefined;
      let hasMore = true;
      while (hasMore) {
        const res: StripePlansResponse = await api.commissionBilling.getStripePlans(100, cursor);
        all.push(...res.data);
        hasMore = res.hasMore;
        cursor = res.nextCursor ?? undefined;
      }
      return all;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

// =============================================================================
// INVOICE EMAIL PREVIEW (the standalone Autumn invoice / value-receipt bill)
// =============================================================================

export const useInvoiceEmailPreview = (
  userId: string,
  month: number,
  year: number,
  enabled: boolean,
  options?: {
    collectionMethod?: "charge_automatically" | "send_invoice";
    daysUntilDue?: number;
  },
) => {
  const { collectionMethod, daysUntilDue } = options ?? {};
  return useQuery({
    // collectionMethod/daysUntilDue are in the key so toggling the billing method
    // re-fetches instead of serving the stale projected HTML.
    queryKey: [
      "invoice-email-preview",
      userId,
      month,
      year,
      collectionMethod,
      daysUntilDue,
    ],
    queryFn: () =>
      api.commissionBilling.getInvoiceEmailPreview({
        userId,
        month,
        year,
        collectionMethod,
        daysUntilDue,
      }),
    enabled,
    // The projected bill tracks the charge preview, so keep it lightly cached.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

// =============================================================================
// CHARGE PREVIEW
// =============================================================================

export const fetchChargePreview = async (params: {
  userId: string;
  month: string;
  year: string;
}) => {
  return api.commissionBilling.getChargePreview(params);
};

// =============================================================================
// BULK CHARGE HOOKS
// =============================================================================

export function useChargeBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userIds,
      month,
      year,
      totpCode,
    }: {
      userIds: string[];
      month: number;
      year: number;
      totpCode?: string;
    }) => api.commissionBilling.chargeBulk(userIds, month, year, totpCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });
}

export function useBulkChargeStatus(jobId: string | null) {
  return useQuery({
    queryKey: ["bulkChargeStatus", jobId],
    queryFn: () => api.commissionBilling.getBulkChargeStatus(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data?.data;
      return data?.status === "completed" ? false : 2000;
    },
  });
}

export function useStripeSubscriptions(userId: string | undefined) {
  return useQuery({
    queryKey: ["stripe-subscriptions", userId],
    queryFn: async () => {
      const res = await api.commissionBilling.getUserSubscriptions(userId!);
      return (res.data || []) as StripeSubscription[];
    },
    enabled: Boolean(userId),
  });
}

/** Cancels immediately. Marks the cached row canceled on success (no refetch —
 *  Stripe's list is eventually consistent right after a cancel). */
export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscriptionId: string) =>
      api.commissionBilling.cancelSubscription(subscriptionId),
    onSuccess: (_d, subscriptionId) => {
      queryClient.setQueriesData<StripeSubscription[]>(
        { queryKey: ["stripe-subscriptions"] },
        (prev) =>
          prev?.map((s) =>
            s.id === subscriptionId ? { ...s, status: "canceled" } : s,
          ),
      );
    },
    onError: () => {
      ShowToast({ type: "error", message: "Failed to cancel subscription" });
    },
  });
}
